import { ASSETS } from './assets';

export function BookSectionView({ piece, crossRefs, onBack, onNavigate, onNext, onPrev, currentIndex, totalPieces, studiedCount, discount, selectedBook }) {
  const paragraphs = (piece.book_section_text || '').split('\n').filter(p => p.trim());

  // Use discount-based pricing if available
  const hasDiscount = discount && discount.discount_pct > 0;
  const currentPrice = hasDiscount ? discount.final_price : (() => {
    const advancementPrices = [4.99, 5.99, 6.99, 7.99, 8.99, 9.99, 10.99, 11.99, 12.99, 14.99];
    return advancementPrices[Math.min((studiedCount || 0), advancementPrices.length - 1)];
  })();
  const isMaxPrice = !hasDiscount && currentPrice >= 14.99;
  const bundlePrice = 24;

  return (
    <div className="min-h-screen fade-up">
      {/* Top navigation */}
      <div className="max-w-4xl mx-auto px-4 pt-6 pb-4">
        <button onClick={onBack} data-testid="btn-back-to-scroll"
          className="flex items-center gap-2 text-amber-600 hover:text-amber-300 transition-colors text-sm">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back to Scroll
        </button>
      </div>

      {/* Scroll content with real scroll image background */}
      <div className="max-w-4xl mx-auto px-3 sm:px-4 pb-8">
        <div className="relative rounded-xl overflow-visible" style={{ boxShadow: '0 0 100px rgba(180,120,40,0.15), 0 40px 100px rgba(0,0,0,0.5)' }}>

          {/* Full scroll image as background */}
          <div className="absolute inset-0 z-0">
            <img src={ASSETS.scrollFrame} alt=""
              className="w-full h-full object-cover pointer-events-none"
              style={{ opacity: 0.35 }}
              data-testid="book-section-scroll-bg" />
          </div>
          {/* Warm overlay that lets the scroll show through */}
          <div className="absolute inset-0 z-[1]" style={{ background: 'linear-gradient(180deg, rgba(20,14,6,0.65) 0%, rgba(18,12,5,0.6) 20%, rgba(18,12,5,0.6) 80%, rgba(20,14,6,0.65) 100%)' }} />

          {/* Scroll roll top */}
          <div className="h-8 sm:h-10 relative overflow-hidden z-20">
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #5c3d1a 0%, #3a2510 40%, #2a1a0a 100%)' }} />
            <div className="absolute inset-0" style={{ background: 'repeating-linear-gradient(90deg, transparent, transparent 6px, rgba(90,60,20,0.3) 6px, rgba(90,60,20,0.3) 7px)' }} />
            <div className="absolute bottom-0 left-0 right-0 h-2" style={{ background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.3))' }} />
          </div>

          {/* Content area */}
          <div className="relative z-10 px-6 sm:px-12 lg:px-20 py-10 sm:py-14">

            {/* Header */}
            <div className="text-center mb-10">
              <p className="text-xs sm:text-sm tracking-[0.4em] uppercase font-serif mb-4" style={{ color: '#c8a55a' }}>From {selectedBook?.title || 'The Book'}</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold mb-3" style={{ color: '#edd9a3' }}>{piece.reference}</h2>
              <p className="font-serif italic text-base sm:text-lg mb-6" style={{ color: '#d4b06a' }}>"{piece.anchor}"</p>
              <div className="h-px w-32 mx-auto" style={{ background: 'linear-gradient(90deg, transparent, #8B6914 30%, #c8a55a 50%, #8B6914 70%, transparent)' }} />
            </div>

            {/* Book content paragraphs */}
            <div className="max-w-2xl mx-auto space-y-5">
              {paragraphs.map((para, i) => {
                const t = para.trim();
                const isQuote = t.startsWith('"') || t.startsWith('\u201c');
                const isRef = t.startsWith('—') || t.startsWith('\u2014');
                const isHeading = t === t.toUpperCase() && t.length < 60 && t.length > 3;
                const isBullet = /^(The coming|Our gathering|"That day"|The rebellion|The man of)/.test(t);
                const isVerdict = t.includes('VERDICT');
                const isGit = /^(GRAMMAR INTEGRITY|SUBJECT|VERB|OBJECT)/.test(t);

                if (isHeading) return <h3 key={i} className="text-center font-serif font-bold text-base sm:text-lg tracking-[0.15em] pt-4" style={{ color: '#d4a84a' }}>{t}</h3>;
                if (isVerdict) return <div key={i} className="text-center py-4 px-6 my-4 rounded-lg border" style={{ borderColor: 'rgba(180,130,50,0.25)', background: 'rgba(120,80,20,0.08)' }}><p className="font-serif text-sm sm:text-base leading-relaxed" style={{ color: '#e8d5a8' }}>{t}</p></div>;
                if (isGit) return <p key={i} className="font-serif font-bold text-sm sm:text-base pt-3" style={{ color: '#c8a55a' }}>{t}</p>;
                if (isQuote) return <blockquote key={i} className="pl-5 border-l-2 my-4" style={{ borderColor: '#8B6914' }}><p className="font-serif italic text-sm sm:text-base leading-[2]" style={{ color: '#e8d5a8' }}>{t}</p></blockquote>;
                if (isRef) return <p key={i} className="text-right font-serif italic text-xs sm:text-sm pr-4" style={{ color: '#a08040' }}>{t}</p>;
                if (isBullet) return <p key={i} className="font-serif text-sm sm:text-base leading-[1.8] pl-6" style={{ color: '#d4c4a0' }}><span style={{ color: '#c8a55a' }}>&#x2756; </span>{t}</p>;
                return <p key={i} className="font-serif text-sm sm:text-base leading-[2]" style={{ color: '#d4c4a0' }}>{t}</p>;
              })}
            </div>

            {/* End of scroll divider */}
            <div className="text-center mt-10 mb-8">
              <div className="h-px w-32 mx-auto mb-4" style={{ background: 'linear-gradient(90deg, transparent, #8B6914 30%, #c8a55a 50%, #8B6914 70%, transparent)' }} />
              <p className="text-xs font-serif tracking-[0.3em] uppercase" style={{ color: '#8B6914' }}>End of Scroll</p>
            </div>

            {/* BUY THE EBOOK CTA */}
            <div className="max-w-xl mx-auto mb-10" data-testid="buy-ebook-cta">
              <div className="rounded-xl p-6 sm:p-8 text-center border" style={{ borderColor: isMaxPrice ? 'rgba(239,68,68,0.3)' : 'rgba(217,119,6,0.25)', background: isMaxPrice ? 'linear-gradient(135deg, rgba(127,29,29,0.15), rgba(30,20,8,0.8), rgba(127,29,29,0.15))' : 'linear-gradient(135deg, rgba(120,53,15,0.15), rgba(30,20,8,0.8), rgba(120,53,15,0.15))' }}>

                {isMaxPrice ? (
                  <>
                    <p className="text-[10px] tracking-[0.3em] uppercase font-mono mb-3" style={{ color: '#ef4444' }}>You've Reached the Edge of the Map</p>
                    <h3 className="text-xl sm:text-2xl font-serif font-bold mb-2" style={{ color: '#fcd34d' }}>To Continue, Choose Your Path</h3>
                    <p className="text-sm text-amber-200/60 font-serif leading-relaxed mb-6 max-w-sm mx-auto">
                      You've explored all the puzzle pieces. To unlock the full book, the companion study, and all the deeper materials — choose one of these paths:
                    </p>

                    {/* Two main options side by side */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-5">
                      <button data-testid="btn-buy-ebook"
                        onClick={() => onNavigate('/buy')}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-base font-serif font-semibold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(217,119,6,0.3)]"
                        style={{ background: 'linear-gradient(135deg, #b45309, #d97706, #b45309)', color: '#fff', boxShadow: '0 4px 20px rgba(217,119,6,0.25)' }}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        Buy the Ebook — $14.99
                      </button>

                      <span className="text-amber-700/40 font-serif text-sm italic">or</span>

                      <button data-testid="btn-become-berean"
                        onClick={() => onNavigate('/subscriptions')}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-base font-serif font-semibold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(59,130,246,0.2)]"
                        style={{ background: 'linear-gradient(135deg, #1e3a5f, #2563eb, #1e3a5f)', color: '#fff', boxShadow: '0 4px 20px rgba(37,99,235,0.2)' }}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Become a Berean — $9/mo
                      </button>
                    </div>

                    <p className="text-[10px] text-amber-600/40 font-serif max-w-sm mx-auto leading-relaxed">
                      Berean members get full access to the book, companion, all appendices, and the interactive curriculum.
                    </p>

                    {/* Bundle option */}
                    <div className="pt-4 mt-4 border-t" style={{ borderColor: 'rgba(139,105,20,0.15)' }}>
                      <button data-testid="btn-buy-bundle"
                        onClick={() => onNavigate('/buy')}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-serif transition-all duration-300 border hover:border-amber-500/50 hover:text-amber-200"
                        style={{ borderColor: 'rgba(217,119,6,0.2)', color: '#a08040', background: 'rgba(120,53,15,0.05)' }}>
                        Book + Companion Bundle — ${bundlePrice}.00
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-[10px] tracking-[0.3em] uppercase font-mono mb-3" style={{ color: '#d97706' }}>Continue Your Journey</p>
                    <h3 className="text-xl sm:text-2xl font-serif font-bold mb-2" style={{ color: '#fcd34d' }}>Unlock the Full Book</h3>
                    <p className="text-sm text-amber-200/60 font-serif leading-relaxed mb-5 max-w-sm mx-auto">
                      Get the complete ebook and access this entire interactive curriculum — all ten puzzle pieces, the Grammar Integrity Tests, and the deeper study materials.
                    </p>

                    {/* Main buy button */}
                    <button data-testid="btn-buy-ebook"
                      onClick={() => onNavigate('/buy')}
                      className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-base font-serif font-semibold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(217,119,6,0.3)] mb-4"
                      style={{ background: 'linear-gradient(135deg, #b45309, #d97706, #b45309)', color: '#fff', boxShadow: '0 4px 20px rgba(217,119,6,0.25)' }}>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      Buy the Ebook — ${currentPrice.toFixed(2)}
                    </button>

                    {/* Bundle offer */}
                    <div className="pt-4 border-t" style={{ borderColor: 'rgba(139,105,20,0.2)' }}>
                      <p className="text-xs text-amber-400/50 font-serif mb-2">Want the complete study experience?</p>
                      <button data-testid="btn-buy-bundle"
                        onClick={() => onNavigate('/buy')}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-serif transition-all duration-300 border hover:border-amber-500/50 hover:text-amber-200"
                        style={{ borderColor: 'rgba(217,119,6,0.3)', color: '#d4a84a', background: 'rgba(120,53,15,0.08)' }}>
                        Book + Companion Bundle — ${bundlePrice}.00
                      </button>
                      <p className="text-[10px] text-amber-600/40 font-serif mt-3 max-w-xs mx-auto leading-relaxed">
                        The Companion teaches the same way this app does — verse by verse, piece by piece.
                      </p>
                    </div>

                    {/* Membership upsell */}
                    <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(139,105,20,0.1)' }}>
                      <button onClick={() => onNavigate('/subscriptions')} data-testid="btn-membership-upsell"
                        className="text-[11px] font-serif text-amber-600/50 hover:text-amber-400 transition-colors underline decoration-amber-800/30 underline-offset-2">
                        Or become a member for full access to all resources
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Bottom Navigation — Back + Prev/Next */}
            <div className="flex items-center justify-between gap-3 pt-6 border-t" style={{ borderColor: 'rgba(139,105,20,0.2)' }} data-testid="book-section-bottom-nav">
              {onPrev ? (
                <button onClick={onPrev} data-testid="btn-book-prev-piece"
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-full transition-all duration-300 font-serif text-sm border border-amber-900/30 text-amber-400 hover:border-amber-600/50 hover:text-amber-200 hover:shadow-[0_0_20px_rgba(217,119,6,0.1)]">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  Prev Piece
                </button>
              ) : <div className="w-24" />}

              <button onClick={onBack} data-testid="btn-book-back-to-scroll"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-300 font-serif text-sm border border-amber-800/30 text-amber-500 hover:border-amber-600/50 hover:text-amber-200"
                style={{ background: 'linear-gradient(135deg, rgba(120,53,15,0.08), rgba(217,119,6,0.03))' }}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                Back to Scroll
              </button>

              {onNext ? (
                <button onClick={onNext} data-testid="btn-book-next-piece"
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-full transition-all duration-300 font-serif text-sm hover:shadow-[0_0_20px_rgba(217,119,6,0.15)]"
                  style={{ border: '1px solid rgba(217,119,6,0.4)', background: 'linear-gradient(135deg, rgba(120,53,15,0.15), rgba(217,119,6,0.05))', color: '#fbbf24' }}>
                  Next Piece
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              ) : <div className="w-24" />}
            </div>
          </div>

          {/* Scroll roll bottom */}
          <div className="h-8 sm:h-10 relative overflow-hidden z-20">
            <div className="absolute top-0 left-0 right-0 h-2" style={{ background: 'linear-gradient(0deg, transparent, rgba(0,0,0,0.3))' }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, #5c3d1a 0%, #3a2510 40%, #2a1a0a 100%)' }} />
            <div className="absolute inset-0" style={{ background: 'repeating-linear-gradient(90deg, transparent, transparent 6px, rgba(90,60,20,0.3) 6px, rgba(90,60,20,0.3) 7px)' }} />
          </div>
        </div>
      </div>

      {/* Appendices below scroll */}
      {crossRefs.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 pb-12">
          <p className="text-center text-[10px] tracking-[0.2em] uppercase text-amber-600/50 mb-4 font-serif">Ready to go deeper? Explore the Appendices</p>
          <div className="max-w-lg mx-auto space-y-2">
            {crossRefs.slice(0, 5).map((ref, i) => (
              <button key={i} onClick={() => onNavigate(`/ebooks/${ref.target_book_id}/${ref.target_chapter_order || 1}`)}
                data-testid={`book-section-ref-${ref.appendix_code || i}`}
                className="flex items-center gap-3 w-full text-left p-3 rounded-lg transition-all duration-300 group/ref border border-amber-900/20 hover:border-amber-600/30"
                style={{ background: 'linear-gradient(135deg, rgba(15,10,5,0.8), rgba(20,15,8,0.5))' }}>
                <span className="flex-shrink-0 px-2 py-1 rounded text-[10px] font-bold font-mono" style={{ backgroundColor: `${piece.color}15`, color: piece.color }}>{ref.appendix_code}</span>
                <span className="text-sm text-amber-200/60 group-hover/ref:text-amber-100 transition-colors font-serif">{ref.description?.substring(0, 80) || ref.target_chapter_title}</span>
                <svg className="w-4 h-4 text-amber-800 group-hover/ref:text-amber-400 flex-shrink-0 ml-auto transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
